import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import constant from "./utils/constant";
import { CircleWithIcon } from "./Common";
import { MicIcon } from "../../images";
import { second_to_time } from "./utils/functions";
import "@lottiefiles/lottie-player";
import * as recording_json from "../../images/demo/main_section/RecordAnimation";
import * as typingIndicator_json from "../../images/demo/main_section/TypingIndicator";

const back_color = constant.COLOR.BACK_COLOR_OF_CIRCLE_HAS_ICON;

const RecordingBoxHeader = (props) => {
  const { classNames, stopRecording, canvasRef, ...restProps } = props;

  const [seconds, setSeconds] = useState(0);
  const [isMobile, setIsMobile] =  useState(window.innerWidth <= 600);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 600);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const StopRectangle = () => {
    return <div className="stop-rectangle" />;
  };

  const RecordingLottiePlayer = () => {
    // use from lottie-player package: https://github.com/LottieFiles/lottie-player
    return (
      <div>
        <canvas
          style={{ transform: "rotate(180deg)" }}
          ref={canvasRef}
          width={isMobile ? "200" : "400"}
          height="60"
        />
      </div>
    );
  };

  useEffect(() => {
    const interval = setInterval(
      () => setSeconds((prevSecond) => prevSecond + 1),
      1000
    );
    return () => clearInterval(interval);
  }, []);

  return (
    // set key for detect difference between elements that has `header` classname that cause work animation in every rerender.
    // when the div re-renders, react only changes its inner text. Adding a key will make react think it's a different div when the key changes, so it'll unmount it and mount again.
    <div
      key="recording-header"
      className={`header ${classNames}`}
      {...restProps}
    >
      <CircleWithIcon
        classNames="stop-wrapper"
        backColor={back_color.ACTIVE_SMALL_LINK}
        onClickFunc={stopRecording}
        ChildComponent={StopRectangle}
        title="برای پایان صحبت، دکمه را فشار دهید"
      />
      <div
        style={
          isMobile
            ? {
                width: "200",
                height: "31px",
                marginRight: "1rem",
                marginLeft: "0.5rem",
              }
            : {
                width: "400",
                height: "31px",
                marginRight: "1rem",
                marginLeft: "0.5rem",
              }
        }
      >
        <RecordingLottiePlayer />
      </div>
      <div
        className="time-section"
        style={{ backgroundColor: back_color.ACTIVE_MIC }}
      >
        <span className="time">{second_to_time(seconds)}</span>
      </div>
    </div>
  );
};

RecordingBoxHeader.propTypes = {
  classNames: PropTypes.string,
  stopRecording: PropTypes.func.isRequired,
  canvasRef: PropTypes.any,
};

RecordingBoxHeader.defaultProps = {
  classNames: "",
};

const Recording = (props) => {
  const { stopRecording, recordingResponse, wsConnected, canvasRef } = props;

  const text_wrapper_ref = useRef(null);

  useEffect(() => {
    // handle add has-scroll class when response-text-wrapper element has scroll.
    const text_wrapper_el = text_wrapper_ref.current;
    const child_el = text_wrapper_el.children[0];
    const hasVerticalScrollbar =
      child_el.scrollHeight >= text_wrapper_el.clientHeight;
    if (hasVerticalScrollbar) {
      text_wrapper_el.classList.add("has-scroll");
      child_el.scrollIntoView(false);
    }
  }, [recordingResponse]);

  return (
    <div className="recording">
      <RecordingBoxHeader
        classNames="recording-header"
        stopRecording={stopRecording}
        canvasRef={canvasRef}
      />
      <div ref={text_wrapper_ref} className="response-text-wrapper">
        <div className="recording-text">
          {recordingResponse}
          {wsConnected && (
            <span className="typing-indicator">
              <lottie-player
                autoplay
                loop
                mode="normal"
                src={JSON.stringify(typingIndicator_json.default)}
              />
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

Recording.propTypes = {
  stopRecording: PropTypes.func.isRequired,
  recordingResponse: PropTypes.string,
  wsConnected: PropTypes.bool.isRequired,
  canvasRef: PropTypes.any,
};

const RecordBox = (props) => {
  const {
    recording,
    stopRecording,
    startRecording,
    recordingResponse,
    wsConnected,
    canvasRef,
  } = props;
  if (recording) {
    return (
      <Recording
        stopRecording={stopRecording}
        recordingResponse={recordingResponse}
        wsConnected={wsConnected}
        canvasRef={canvasRef}
      />
    );
  }
  return (
    <div className="initial-state record">
      <CircleWithIcon
        ChildComponent={MicIcon}
        backColor={back_color.ACTIVE_MIC}
        classNames="icon-wrapper mic-icon"
        onClickFunc={startRecording}
      />
      <div className="text">
        <div className="in-web">
          <div>برای شروع به صحبت، دکمه را فشار دهید</div>
          <div>متن پیاده شده آن، در اینجا ظاهر شود</div>
        </div>
        <div className="in-mobile">
          <div>برای شروع به صحبت،</div>
          <div>دکمه را فشار دهید</div>
        </div>
      </div>
    </div>
  );
};

RecordBox.propTypes = {
  recording: PropTypes.bool,
  stopRecording: PropTypes.func.isRequired,
  startRecording: PropTypes.func.isRequired,
  recordingResponse: PropTypes.string,
  wsConnected: PropTypes.bool.isRequired,
  canvasRef: PropTypes.any,
};

RecordBox.defaultProps = {
  recording: false,
};

export default RecordBox;
